import { O } from '@angular/cdk/keycodes';
import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { faPalette } from '@fortawesome/pro-light-svg-icons';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { MediaHandlerService } from 'src/app/handlers/media-handler.service';
import { NotificationService } from 'src/app/notifications/notification.service';
import { AWSService } from 'src/app/shared/aws.service';
import { SessionService } from 'src/app/session.service';
import { UiService } from 'src/app/ui/ui.service';
import { IntegrationDTO } from '../Integration';
import { IntegrationProviderDTO } from '../IntegrationProviderDTO';
import { IntegrationsService } from '../integrations.service';

@Component({
  selector: 'app-integration',
  templateUrl: './integration.component.html',
  styleUrls: ['./integration.component.scss']
})
export class IntegrationComponent implements OnInit {
  newIntegration!: boolean;
  integration?: IntegrationDTO;
  integrationForm!: FormGroup;
  providers?: IntegrationProviderDTO[];
  configs: any[] = [];
  existingIntegrations: IntegrationDTO[] = [];
  referenceOptions: { [fieldId: string]: any[] } = {};
  uploadErrors: { [fieldId: string]: string } = {};
  uploadingFieldId: string | null = null;
  activeColorFieldId: string | null = null;
  colorPickerColor: string = '';
  faPalette = faPalette;
  private onComponentDestroy = new Subject();

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private uiService: UiService,
    private integrationService: IntegrationsService,
    private mediaHandlerService: MediaHandlerService,
    private awsService: AWSService,
    private sessionService: SessionService,
    private notificationService: NotificationService,
  ) { }

  ngOnInit(): void {
    this.integration = this.activatedRoute.snapshot.data.integration || {
      config: {}
    };
    this.newIntegration = this.activatedRoute.snapshot.data.integration == undefined;
    this.providers = this.activatedRoute.snapshot.data.providers;
    this.loadExistingIntegrations();
    this.initForm();
  }

  loadExistingIntegrations() {
    if (this.newIntegration) {
      this.integrationService.getIntegrations(
        this.currentAccount.accountID,
        this.sessionService.currentAppValue?.id
      ).pipe(takeUntil(this.onComponentDestroy))
      .subscribe(integrations => {
        this.existingIntegrations = Array.isArray(integrations) ? integrations : (integrations as any).data || [];
      });
    }
  }

  validateProviderSelection(providerId: number) {
    if (this.newIntegration && providerId && this.existingIntegrations.length > 0) {
      const existingIntegration = this.existingIntegrations.find(
        integration => integration.integration_provider_id === providerId
      );
      
      if (existingIntegration) {
        this.integrationForm.get('provider')?.setErrors({ 
          providerAlreadyExists: true 
        });
      } else {
        // Clear the custom error if provider is valid
        const providerControl = this.integrationForm.get('provider');
        if (providerControl?.errors?.['providerAlreadyExists']) {
          delete providerControl.errors['providerAlreadyExists'];
          if (Object.keys(providerControl.errors).length === 0) {
            providerControl.setErrors(null);
          }
        }
      }
    }
  }

  initForm() {
    this.integrationForm = new FormGroup({
      provider: new FormControl(this.integration?.integration_provider_id, [Validators.required]),
    });

    // Disable provider selection for existing integrations
    if (!this.newIntegration) {
      this.integrationForm.get("provider")?.disable();
    }

    this.integrationForm.get("provider")!.valueChanges.subscribe(value => {
      this.validateProviderSelection(value);
      this.initConfig();
    });

    this.initConfig();
  }

  initConfig() {
    this.configs = [];
    if(this.integrationForm.get("provider")?.value !== null){
      let provider = this.providers?.find((p)=>{
        return p.id == this.integrationForm.get("provider")?.value
      })
      for(var f in provider!.config.fields) {
        var field = provider!.config.fields[f];
        var rawVal = this.integration!.config[field.id] != undefined ? this.integration!.config[field.id] : (field.default != undefined ? field.default : null);
        var val = this.normalizeConfigFieldValue(field, rawVal);
        this.integrationForm.addControl(
          field.id,
          new FormControl(val, field.required ? [
            Validators.required,
          ] : null),
        )
        this.configs.push(field);

        if (field.type === 'integration_reference' && field.filter_types) {
          this.loadReferenceOptions(field);
        }
      }
    }
  }

  loadReferenceOptions(field: any) {
    const appId = this.newIntegration
      ? this.sessionService.currentAppValue?.id
      : this.integration?.account_app_id;
    const accountId = this.currentAccount.accountID;
    const allOptions: any[] = [];
    let pendingRequests = field.filter_types.length;

    field.filter_types.forEach((type: string) => {
      this.integrationService.getIntegrations(accountId, appId, false, type)
        .pipe(takeUntil(this.onComponentDestroy))
        .subscribe((results: any) => {
          const items = Array.isArray(results) ? results : (results?.data || []);
          items.forEach((item: any) => {
            allOptions.push({
              id: item.id,
              providerName: item.provider?.name || item.name || `Integration #${item.id}`
            });
          });
          pendingRequests--;
          if (pendingRequests <= 0) {
            this.referenceOptions[field.id] = allOptions;
          }
        });
    });
  }

  get saveDisabled() {
    return this.integrationForm.invalid;
  }

  get providerAlreadyExistsError() {
    return this.integrationForm.get('provider')?.errors?.['providerAlreadyExists'];
  }

  isProviderDisabled(providerId: number): boolean {
    if (!this.newIntegration) return false;
    return this.existingIntegrations.some(
      integration => integration.integration_provider_id === providerId
    );
  }

  getProviderDisabledState(provider: any): boolean {
    return this.isProviderDisabled(provider.id);
  }

  get currentAccount() {
    return this.sessionService.currentAccountValue!;
  }

  get currentAccountAppId() {
    return this.newIntegration
      ? this.sessionService.currentAppValue?.id
      : this.integration?.account_app_id;
  }

  get integrationDTO() {
    // Get provider ID from form control or existing integration
    const providerId = this.integrationForm.get("provider")?.value || this.integration?.integration_provider_id;
    
    let provider = this.providers?.find((p) => {
      return p.id == providerId;
    });

    let integrationDTO: any = {
      id: this.integration ? this.integration.id : null,
      account_app_id: this.newIntegration ? this.sessionService.currentAppValue?.id : this.integration!.account_app_id,
      integration_provider_id: provider!.id,
      config: {}
    }

    for(var f in provider!.config.fields) {
      var field = provider!.config.fields[f];
      var val = this.integration!.config[field.id] != undefined ? this.integration!.config[field.id] : null;
      integrationDTO.config[field.id] = this.serializeConfigFieldValue(field, this.integrationForm.get(field.id)?.value);
    }

    return integrationDTO;
  }

  private normalizeConfigFieldValue(field: any, value: any) {
    if ((field?.type === 'textarea' || field?.type === 'json') && value !== null && typeof value === 'object') {
      try {
        return JSON.stringify(value, null, 2);
      } catch {
        return String(value);
      }
    }
    return value;
  }

  private serializeConfigFieldValue(field: any, value: any) {
    if ((field?.type !== 'textarea' && field?.type !== 'json') || typeof value !== 'string') {
      return value;
    }

    const trimmed = value.trim();
    if (!trimmed) {
      return value;
    }

    try {
      return JSON.parse(trimmed);
    } catch {
      return value;
    }
  }

  private canonicalMimeType(token: string): string {
    const normalized = token.trim().toLowerCase();
    if (!normalized) {
      return normalized;
    }
    if (normalized === 'file/text') {
      return 'text/plain';
    }
    if (normalized === 'document/html') {
      return 'text/html';
    }
    return normalized;
  }

  private parseAllowedMimeTypes(field: any): string[] {
    const raw = field?.allowed;
    if (!raw) {
      return [];
    }
    const tokens = String(raw).split(/[;,]/).map((part) => part.trim()).filter(Boolean);
    return tokens.map((token) => this.canonicalMimeType(token));
  }

  private mimeTypeAllowed(fileType: string, allowed: string[]): boolean {
    const normalized = (fileType || '').toLowerCase();
    if (!normalized) {
      return false;
    }
    return allowed.some((allowedType) => {
      if (allowedType.endsWith('/*')) {
        const prefix = allowedType.slice(0, allowedType.length - 1);
        return normalized.startsWith(prefix);
      }
      return allowedType === normalized;
    });
  }

  getAcceptForField(field: any): string {
    const allowed = this.parseAllowedMimeTypes(field);
    return allowed.join(',');
  }

  private extensionAllowed(fileName: string, field: any): boolean {
    const ext = (fileName.split('.').pop() || '').toLowerCase();
    if (!ext) {
      return false;
    }

    const raw = String(field?.allowed || '').toLowerCase();
    if (raw.includes('document/html') || raw.includes('text/html')) {
      if (['html', 'htm', 'hbs', 'handlebars'].includes(ext)) {
        return true;
      }
    }
    if (raw.includes('text/css')) {
      if (ext === 'css') {
        return true;
      }
    }
    if (raw.includes('file/text') || raw.includes('text/plain')) {
      if (ext === 'txt') {
        return true;
      }
    }
    if (raw.includes('image/')) {
      if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext)) {
        return true;
      }
    }
    return false;
  }

  async onAccountUploadSelected(field: any, event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }

    this.uploadErrors[field.id] = '';
    const allowed = this.parseAllowedMimeTypes(field);
    if (allowed.length > 0) {
      const fileType = (file.type || '').toLowerCase();
      const mimeAllowed = this.mimeTypeAllowed(fileType, allowed);
      const extensionAllowed = this.extensionAllowed(file.name, field);
      if (!mimeAllowed && !extensionAllowed) {
        this.uploadErrors[field.id] = `Unsupported file type (${file.type || 'unknown'}). Allowed: ${field.allowed}`;
        input.value = '';
        return;
      }
    }

    const folder = field.path || 'assistist/pdf';
    this.uploadingFieldId = field.id;

    try {
      const signedResponse: any = await this.mediaHandlerService.signUpload(this.currentAccount.accountID, {
        file: {
          size: file.size,
          name: file.name,
          type: file.type || 'application/octet-stream',
        },
        folder,
        account_app_id: this.currentAccountAppId || null,
      }).pipe(take(1)).toPromise();

      await this.awsService.uploadImage(signedResponse.signed_request, file).pipe(take(1)).toPromise();
      const resolvedUrl = signedResponse.public_url || signedResponse.url;
      this.integrationForm.get(field.id)?.setValue(resolvedUrl);
      this.notificationService.openSuccessSnackBar('File uploaded');
    } catch (error: any) {
      this.uploadErrors[field.id] = error?.error?.message || error?.message || 'File upload failed';
    } finally {
      this.uploadingFieldId = null;
      input.value = '';
    }
  }

  async copyFieldUrl(fieldId: string) {
    const value = this.integrationForm.get(fieldId)?.value;
    const url = typeof value === 'string' ? value.trim() : '';
    if (!url) {
      this.notificationService.openSuccessSnackBar('No URL to copy');
      return;
    }

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = url;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'absolute';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      this.notificationService.openSuccessSnackBar('URL copied');
    } catch {
      this.notificationService.openSuccessSnackBar('Unable to copy URL');
    }
  }

  async deleteAccountUploadFile(field: any) {
    const control = this.integrationForm.get(field.id);
    const value = control?.value;
    const url = typeof value === 'string' ? value.trim() : '';
    if (!url) {
      return;
    }

    const confirmed = window.confirm('Delete the uploaded file from S3 and clear this field?');
    if (!confirmed) {
      return;
    }

    this.uploadErrors[field.id] = '';
    this.uploadingFieldId = field.id;

    try {
      await this.mediaHandlerService.deleteUpload(this.currentAccount.accountID, {
        url,
        account_app_id: this.currentAccountAppId || null,
      })
        .pipe(take(1))
        .toPromise();
      control?.setValue(null);
      this.notificationService.openSuccessSnackBar('File deleted');
    } catch (error: any) {
      this.uploadErrors[field.id] = error?.error?.message || error?.message || 'File delete failed';
    } finally {
      this.uploadingFieldId = null;
    }
  }

  openColorPicker(fieldId: string) {
    this.activeColorFieldId = fieldId;
    const currentValue = this.integrationForm.get(fieldId)?.value;
    this.colorPickerColor = typeof currentValue === 'string' && currentValue
      ? currentValue
      : '#000000';
  }

  onColorPickerSelected() {
    if (!this.activeColorFieldId || !this.colorPickerColor) {
      return;
    }
    this.integrationForm.get(this.activeColorFieldId)?.setValue(this.colorPickerColor);
  }

  cancel() {
    this.router.navigate(["list"], {
      relativeTo: this.activatedRoute.parent!.parent,
    });
  }

  saveNewIntegration() {
    let dto = this.integrationDTO as IntegrationDTO;
    this.uiService
      .waitForEvent(
        this.integrationService.createIntegration(this.currentAccount.accountID, dto),
      )
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe(
        (saved) => {
          this.notificationService.openSuccessSnackBar(
            "Integration created",
          );
          this.router.navigate([saved.id], {
            relativeTo: this.activatedRoute.parent,
          });
        }
      )
  }

  saveIntegrationUpdates() {
    let dto = this.integrationDTO as IntegrationDTO;
    this.uiService
      .waitForEvent(
        this.integrationService.updateIntegration(this.currentAccount.accountID, dto),
      )
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe(
        () => {
          this.notificationService.openSuccessSnackBar(
            "Integration updated successfully",
          );
        }
      )
  }

  deleteIntegration() {

  }

  generateSecret(fieldId: string) {
    if (!this.integration?.id) {
      this.notificationService.openSuccessSnackBar(
        "Please save the integration first before generating a secret."
      );
      return;
    }
    this.integrationService.generateSecret(
      this.currentAccount.accountID,
      this.integration.id
    ).pipe(takeUntil(this.onComponentDestroy))
    .subscribe((result: any) => {
      this.integrationForm.get(fieldId)?.setValue(result.secret);
      this.notificationService.openSuccessSnackBar("Secret generated successfully");
    });
  }

}
